import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AgCharts } from "ag-charts-react";
import "ag-grid-enterprise";

import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { customChartThemes } from "../../common/grid";
import { localized } from "../../common/utils";

export function ExperienceYearsChart({ persons }) {
  const { t } = useTranslation();
  const [data, setData] = useState();

  useEffect(() => {
    if (!persons || persons.length === 0) return;

    const activePersons = persons.filter(
      p => !p.employmentDetails?.endDate && p.employmentDetails?.startDate
    );

    const buckets = {};
    const today = new Date();

    activePersons.forEach((person) => {
      const startDateStr = person.employmentDetails.startDate;
      if (!startDateStr) return;

      const startDate = new Date(startDateStr);
      if (isNaN(startDate)) return;

      const experienceYears = today.getFullYear() - startDate.getFullYear();
      const bucketStart = Math.floor(experienceYears / 5) * 5;
      const bucketLabel = `${bucketStart}-${bucketStart + 4}`;

      buckets[bucketLabel] = (buckets[bucketLabel] || 0) + 1;
    });

    const chartData = Object.keys(buckets)
      .sort((a, b) => parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]))
      .map(key => ({ experienceBucket: key, count: buckets[key] }));

    setData(chartData);
  }, [persons]);

  const options = {
    title: { text: t("PersonnelExperienceYears", "Henkilöstön kokemusvuodet") },
    data,
    series: [
      {
        type: "bar",
        xKey: "experienceBucket",
        yKey: "count",
        xName: t("ExperienceYears", "Kokemusvuodet"),
        yName: t("AmountOfPersons", "Henkilöt"),
        tooltip: {
          renderer: ({ datum }) => ({
            heading: datum.experienceBucket,
            title: `${datum.count} hlöä`,
            data: [],
          }),
        },
        fill: "#00B8E4",
      },
    ],
    legend: { enabled: false },
    axes: {
      x: { type: "category", title: { text: t("ExperienceYears", "Kokemusvuodet") } },
      y: { type: "number", title: { text: t("AmountOfPersons", "Henkilöt") } },
    },
    theme: customChartThemes.chartTheme,
    overlays: { noData: { text: `${t("No", "Ei")} ${t("data", "tietoja")}` } },
    locale: localized.agChartsLocale,
  };

  return data && data.length > 0 ? <AgCharts options={options} /> : <LoadingContainer />;
}

export function PersonnelAgeDistribution() {
  const { t } = useTranslation();

  const options = {
    title: {
      text: t("PersonnelAgeDistribution", "Henkilöstön ikäjakauma"),
    },
    legend: { enabled: false },
    data: [
      { agegroup: "16-24", share: 56.9 },
      { agegroup: "25-32", share: 22.5 },
      { agegroup: "33-40", share: 6.8 },
      { agegroup: "41-48", share: 8.5 },
      { agegroup: "49-57", share: 2.6 },
      { agegroup: "> 60", share: 1.9 },
    ],
    series: [
      {
        type: "pie",
        calloutLabelKey: "agegroup",
        angleKey: "share",
      },
    ],
    theme: customChartThemes.chartTheme,
    locale: localized.agChartsLocale,
  };

  return <AgCharts options={options} />;
}
